/**
 * Identity Document Configuration Service
 * Provides country-specific document type requirements and validation rules
 */

export type DocumentType = 'passport' | 'national_id' | 'drivers_license';

export interface DocumentRequirement {
  type: DocumentType;
  displayName: string;
  description: string;
  requiresBothSides: boolean;
  requiresSelfie: boolean;
  maxFileSize: number; // in bytes
  acceptedFormats: string[];
  expiryRequired: boolean;
  numberPattern?: RegExp;
  numberExample?: string;
}

export interface CountryDocumentConfig {
  countryCode: string;
  countryName: string;
  supportedDocuments: DocumentRequirement[];
  preferredDocument: DocumentType;
  notes?: string;
}

// Accepted image formats for identity document uploads.
// HEIC/HEIF included because iOS devices use this format by default when
// capturing photos. Without it, mobile users cannot upload camera photos.
const ACCEPTED_IMAGE_FORMATS = [
  'image/jpeg',
  'image/png',
  'image/webp',
  'image/avif',
  'image/heic',
  'image/heif',
];

// Common document configurations
const PASSPORT_CONFIG: DocumentRequirement = {
  type: 'passport',
  displayName: 'Passport',
  description: 'Government-issued passport',
  requiresBothSides: false,
  requiresSelfie: true,
  maxFileSize: 10 * 1024 * 1024, // 10MB
  acceptedFormats: ACCEPTED_IMAGE_FORMATS,
  expiryRequired: true,
  numberPattern: /^[A-Z0-9]{6,12}$/,
  numberExample: 'AB1234567',
};

const NATIONAL_ID_CONFIG: DocumentRequirement = {
  type: 'national_id',
  displayName: 'National ID Card',
  description: 'Government-issued national identity card',
  requiresBothSides: true,
  requiresSelfie: true,
  maxFileSize: 10 * 1024 * 1024, // 10MB
  acceptedFormats: ACCEPTED_IMAGE_FORMATS,
  expiryRequired: true,
  numberPattern: /^[A-Z0-9\-\s]{6,20}$/,
  numberExample: 'ID1234567890',
};

const DRIVERS_LICENSE_CONFIG: DocumentRequirement = {
  type: 'drivers_license',
  displayName: "Driver's License",
  description: 'Government-issued driving license',
  requiresBothSides: true,
  requiresSelfie: true,
  maxFileSize: 10 * 1024 * 1024, // 10MB
  acceptedFormats: ACCEPTED_IMAGE_FORMATS,
  expiryRequired: true,
  numberPattern: /^[A-Z0-9\-\s]{6,20}$/,
  numberExample: 'DL123456789',
};

// Country-specific configurations
const COUNTRY_CONFIGS: CountryDocumentConfig[] = [
  // Switzerland
  {
    countryCode: 'CH',
    countryName: 'Switzerland',
    supportedDocuments: [
      { ...PASSPORT_CONFIG },
      {
        ...NATIONAL_ID_CONFIG,
        displayName: 'Swiss ID Card',
        // Swiss ID cards have various formats:
        // - Old format: 1-2 letters + 7-8 digits (e.g., C12345678, AB1234567)
        // - New biometric (2022+): alphanumeric 8-10 chars
        numberPattern: /^[A-Z0-9]{8,10}$/,
        numberExample: 'C12345678',
      },
      {
        ...DRIVERS_LICENSE_CONFIG,
        displayName: 'Swiss Driving License',
        // Swiss licenses: typically 8-10 digits, may include letters
        numberPattern: /^[A-Z0-9]{7,12}$/,
        numberExample: '12345678',
      },
    ],
    preferredDocument: 'national_id',
    notes:
      'Swiss ID cards are preferred for residents. Passports accepted for all users.',
  },

  // France
  {
    countryCode: 'FR',
    countryName: 'France',
    supportedDocuments: [
      { ...PASSPORT_CONFIG },
      {
        ...NATIONAL_ID_CONFIG,
        displayName: 'French National ID',
        // Old CNI: 12 digits, New CNIe (2021+): alphanumeric EU card format
        numberPattern: /^[A-Z0-9]{9,15}$/,
        numberExample: '123456789012',
      },
      {
        ...DRIVERS_LICENSE_CONFIG,
        displayName: 'French Driving License',
        // French licenses: typically 12-15 characters
        numberPattern: /^[A-Z0-9]{10,15}$/,
        numberExample: '1234567890123',
      },
    ],
    preferredDocument: 'national_id',
  },

  // Germany
  {
    countryCode: 'DE',
    countryName: 'Germany',
    supportedDocuments: [
      { ...PASSPORT_CONFIG },
      {
        ...NATIONAL_ID_CONFIG,
        displayName: 'German ID Card',
        // Pre-2010: 10 digits, Post-2010: 9-10 alphanumeric chars
        numberPattern: /^[A-Z0-9]{9,11}$/,
        numberExample: 'T12345678',
      },
      {
        ...DRIVERS_LICENSE_CONFIG,
        displayName: 'German Driving License',
        // German licenses: typically 10-12 alphanumeric
        numberPattern: /^[A-Z0-9]{10,13}$/,
        numberExample: 'B123456C789',
      },
    ],
    preferredDocument: 'national_id',
  },

  // United States
  {
    countryCode: 'US',
    countryName: 'United States',
    supportedDocuments: [
      { ...PASSPORT_CONFIG },
      {
        ...DRIVERS_LICENSE_CONFIG,
        displayName: 'State-Issued ID/License',
        description: "State-issued driver's license or ID card",
        numberPattern: /^[A-Z0-9\-\s]{6,20}$/,
        numberExample: 'D123-456-789',
      },
    ],
    preferredDocument: 'drivers_license',
    notes:
      "State-issued driver's licenses or ID cards are preferred. No national ID available.",
  },

  // United Kingdom
  {
    countryCode: 'GB',
    countryName: 'United Kingdom',
    supportedDocuments: [
      { ...PASSPORT_CONFIG },
      {
        ...DRIVERS_LICENSE_CONFIG,
        displayName: 'UK Driving License',
        // GB format: 16 chars (DVLA), Northern Ireland: 8 digits (DVA)
        // Accept alphanumeric 8-18 chars to cover all formats
        numberPattern: /^[A-Z0-9]{8,18}$/,
        numberExample: 'SMITH123456A99AB',
      },
    ],
    preferredDocument: 'passport',
    notes:
      'UK does not issue national ID cards. Passport or driving license required.',
  },

  // Italy
  {
    countryCode: 'IT',
    countryName: 'Italy',
    supportedDocuments: [
      { ...PASSPORT_CONFIG },
      {
        ...NATIONAL_ID_CONFIG,
        displayName: 'Italian ID Card',
        // CIE format: 2 letters + 5 digits + 2 letters = 9 chars
        // Old cards may have different formats, allow 8-12 alphanumeric
        numberPattern: /^[A-Z0-9]{8,12}$/,
        numberExample: 'CA12345AA',
      },
      {
        ...DRIVERS_LICENSE_CONFIG,
        displayName: 'Italian Driving License',
        // Italian licenses: typically 10-12 alphanumeric
        numberPattern: /^[A-Z0-9]{9,12}$/,
        numberExample: 'IT1234567A',
      },
    ],
    preferredDocument: 'national_id',
  },

  // Canada
  {
    countryCode: 'CA',
    countryName: 'Canada',
    supportedDocuments: [
      { ...PASSPORT_CONFIG },
      {
        ...DRIVERS_LICENSE_CONFIG,
        displayName: 'Provincial ID/License',
        description: "Provincial driver's license or ID card",
        numberPattern: /^[A-Z0-9\-\s]{6,20}$/,
        numberExample: 'A1234-56789',
      },
    ],
    preferredDocument: 'drivers_license',
    notes:
      "Provincial driver's licenses or ID cards are preferred. No national ID available.",
  },

  // Australia
  {
    countryCode: 'AU',
    countryName: 'Australia',
    supportedDocuments: [
      { ...PASSPORT_CONFIG },
      {
        ...DRIVERS_LICENSE_CONFIG,
        displayName: 'Australian ID/License',
        description: "State/territory driver's license or ID card",
        numberPattern: /^[A-Z0-9\-\s]{6,20}$/,
        numberExample: '12345678',
      },
    ],
    preferredDocument: 'drivers_license',
    notes:
      "State/territory driver's licenses or ID cards are preferred. No national ID available.",
  },

  // Japan
  {
    countryCode: 'JP',
    countryName: 'Japan',
    supportedDocuments: [
      { ...PASSPORT_CONFIG },
      {
        ...DRIVERS_LICENSE_CONFIG,
        displayName: 'Japanese Driving License',
        // Japanese licenses: 12 digits (confirmed format)
        // Allow 10-14 for flexibility with My Number integration
        numberPattern: /^[0-9]{10,14}$/,
        numberExample: '123456789012',
      },
    ],
    preferredDocument: 'passport',
    notes:
      'Japan does not issue national ID cards. Passport or driving license required.',
  },
];

// Default fallback for countries not specifically configured
const DEFAULT_COUNTRY_CONFIG: CountryDocumentConfig = {
  countryCode: 'XX',
  countryName: 'Other',
  supportedDocuments: [
    { ...PASSPORT_CONFIG },
    { ...NATIONAL_ID_CONFIG },
    { ...DRIVERS_LICENSE_CONFIG },
  ],
  preferredDocument: 'passport',
  notes:
    'For countries not specifically configured, all document types are accepted with passport preferred.',
};

/**
 * Identity Document Configuration Service
 */
export class IdentityDocumentConfigService {
  /**
   * Get document configuration for a specific country
   */
  static getCountryConfig(countryCode: string): CountryDocumentConfig {
    const config = COUNTRY_CONFIGS.find(
      (c) => c.countryCode.toLowerCase() === countryCode.toLowerCase(),
    );

    if (!config) {
      return {
        ...DEFAULT_COUNTRY_CONFIG,
        countryCode: countryCode.toUpperCase(),
        countryName: `Country ${countryCode.toUpperCase()}`,
      };
    }

    return config;
  }

  /**
   * Get supported document types for a country
   */
  static getSupportedDocumentTypes(countryCode: string): DocumentType[] {
    const config = this.getCountryConfig(countryCode);
    return config.supportedDocuments.map((doc) => doc.type);
  }

  /**
   * Get document requirement details
   */
  static getDocumentRequirement(
    countryCode: string,
    documentType: DocumentType,
  ): DocumentRequirement | null {
    const config = this.getCountryConfig(countryCode);
    return (
      config.supportedDocuments.find((doc) => doc.type === documentType) || null
    );
  }

  /**
   * Get preferred document type for a country
   */
  static getPreferredDocumentType(countryCode: string): DocumentType {
    const config = this.getCountryConfig(countryCode);
    return config.preferredDocument;
  }

  /**
   * Validate document number format
   */
  static validateDocumentNumber(
    countryCode: string,
    documentType: DocumentType,
    documentNumber: string,
  ): { isValid: boolean; message?: string } {
    const requirement = this.getDocumentRequirement(countryCode, documentType);

    if (!requirement) {
      return {
        isValid: false,
        message: 'Document type not supported for this country',
      };
    }

    if (!requirement.numberPattern) {
      return { isValid: true }; // No specific pattern required
    }

    const isValid = requirement.numberPattern.test(documentNumber);

    return {
      isValid,
      message: isValid
        ? undefined
        : `Invalid format. Example: ${requirement.numberExample || 'N/A'}`,
    };
  }

  /**
   * Get file upload requirements
   */
  static getFileRequirements(
    countryCode: string,
    documentType: DocumentType,
  ): {
    maxFileSize: number;
    acceptedFormats: string[];
    requiresBothSides: boolean;
    requiresSelfie: boolean;
  } | null {
    const requirement = this.getDocumentRequirement(countryCode, documentType);

    if (!requirement) {
      return null;
    }

    return {
      maxFileSize: requirement.maxFileSize,
      acceptedFormats: requirement.acceptedFormats,
      requiresBothSides: requirement.requiresBothSides,
      requiresSelfie: requirement.requiresSelfie,
    };
  }

  /**
   * Get all configured countries
   */
  static getAllConfiguredCountries(): Pick<
    CountryDocumentConfig,
    'countryCode' | 'countryName' | 'preferredDocument'
  >[] {
    return COUNTRY_CONFIGS.map((config) => ({
      countryCode: config.countryCode,
      countryName: config.countryName,
      preferredDocument: config.preferredDocument,
    }));
  }

  /**
   * Check if a country has specific configuration
   */
  static hasSpecificConfig(countryCode: string): boolean {
    return COUNTRY_CONFIGS.some(
      (c) => c.countryCode.toLowerCase() === countryCode.toLowerCase(),
    );
  }
}

// Export types and service
export { IdentityDocumentConfigService as default };
